/**
 * PostHog Service
 *
 * Gère l'initialisation et le tracking des événements PostHog.
 * Respecte le RGPD : PostHog n'est chargé qu'après consentement analytics.
 */

import { isPostHogLoaded } from "../posthog/loadPostHog";

/**
 * Vérifie si PostHog est initialisé et prêt à tracker
 */
export const isPostHogReady = (): boolean => {
  return isPostHogLoaded();
};

/**
 * Track un événement PageView
 * Appelé automatiquement lors des navigations SPA
 * @param path - Chemin de la page (optionnel, par défaut window.location.pathname)
 */
export const trackPageView = (path?: string): void => {
  if (!isPostHogReady()) {
    if (import.meta.env.DEV) {
      console.debug("[PostHog] Not ready, skipping PageView");
    }
    return;
  }

  try {
    const pagePath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
    
    if (typeof window.posthog?.capture !== "function") {
      console.warn("[PostHog] capture function not available, skip PageView");
      return;
    }

    // Track pageview with PostHog
    window.posthog.capture("$pageview", {
      $current_url: typeof window !== "undefined" ? window.location.href : "",
      path: pagePath,
      title: typeof document !== "undefined" ? document.title : "",
    });

    // Log pour debug (production-safe avec console.log pour visibilité)
    console.log("[PostHog] 📊 PageView tracké:", pagePath);
  } catch (error) {
    console.error("[PostHog] Erreur PageView:", error);
  }
};

/**
 * Track un événement personnalisé
 * @param eventName - Nom de l'événement (ex: 'purchase', 'sign_up', 'search')
 * @param properties - Propriétés de l'événement (optionnel)
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  if (!isPostHogReady()) {
    if (import.meta.env.DEV) {
      console.debug(`[PostHog] Not ready, skipping event "${eventName}"`);
    }
    return;
  }

  try {
    if (typeof window.posthog?.capture !== "function") {
      console.warn(`[PostHog] capture function not available, skip event "${eventName}"`);
      return;
    }

    if (properties) {
      window.posthog.capture(eventName, properties);
    } else {
      window.posthog.capture(eventName);
    }

    // Log pour debug (production-safe avec console.debug)
    console.debug(`[PostHog] ✅ Event "${eventName}" tracké:`, properties || "(sans propriétés)");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`[PostHog] Erreur event "${eventName}":`, error);
    }
  }
};

export const trackAgencyEvent = (
  eventName:
    | "availability_confirmed"
    | "booking_accepted"
    | "booking_rejected"
    | "vehicle_published",
  properties: Record<string, unknown>
): void => {
  trackEvent(eventName, properties);
};

/**
 * Identifie un utilisateur avec PostHog
 * @param distinctId - ID unique de l'utilisateur
 * @param properties - Propriétés de l'utilisateur (optionnel)
 */
export const identifyUser = (
  distinctId: string,
  properties?: Record<string, unknown>
): void => {
  if (!isPostHogReady()) {
    if (import.meta.env.DEV) {
      console.debug("[PostHog] Not ready, skipping identify");
    }
    return;
  }

  try {
    if (typeof window.posthog?.identify !== "function") {
      console.warn("[PostHog] identify function not available");
      return;
    }

    if (properties) {
      window.posthog.identify(distinctId, properties);
    } else {
      window.posthog.identify(distinctId);
    }

    console.debug(`[PostHog] ✅ User identified:`, distinctId, properties || "(sans propriétés)");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur identify:", error);
    }
  }
};

/**
 * Identifie un utilisateur Supabase avec PostHog
 * Récupère automatiquement les propriétés depuis l'utilisateur et le profil
 * @param user - Utilisateur Supabase
 * @param role - Rôle de l'utilisateur (optionnel)
 * @param additionalProperties - Propriétés supplémentaires (optionnel)
 */
export const identifyUserFromSupabase = async (
  user: { id: string; email?: string; user_metadata?: Record<string, unknown> },
  role?: string | null,
  additionalProperties?: Record<string, unknown>
): Promise<void> => {
  if (!isPostHogReady()) {
    if (import.meta.env.DEV) {
      console.debug("[PostHog] Not ready, skipping identify");
    }
    return;
  }

  try {
    // Récupérer le rôle depuis les métadonnées si non fourni
    let userRole = role || user.user_metadata?.role as string | undefined;
    
    // Normaliser le rôle
    if (userRole === "host" || userRole === "proprietaire") {
      userRole = "owner";
    }
    if (userRole === "locataire") {
      userRole = "renter";
    }

    // Construire les propriétés de l'utilisateur enrichies
    const userProperties: Record<string, unknown> = {
      role: userRole || undefined,
      agency_name: user.user_metadata?.agency_name || undefined,
      created_at: user.user_metadata?.created_at || new Date().toISOString(),
      account_verified: user.user_metadata?.verified_tenant || user.user_metadata?.verified_host || false,
      is_business_user: userRole === "owner",
      ...additionalProperties,
    };

    // Identifier l'utilisateur
    identifyUser(user.id, userProperties);

    // Track l'événement login si c'est une connexion
    if (additionalProperties?.is_login) {
      trackEvent("login", {
        method: additionalProperties.login_method || "email",
        role: userRole,
      });
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur identifyUserFromSupabase:", error);
    }
  }
};

/**
 * Reset l'identification de l'utilisateur (logout)
 */
export const resetUser = (): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    if (typeof window.posthog?.reset !== "function") {
      console.warn("[PostHog] reset function not available");
      return;
    }

    window.posthog.reset();
    console.debug("[PostHog] ✅ User reset");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur reset:", error);
    }
  }
};

/**
 * Track un événement de conversion (achat/réservation)
 * @param value - Montant de la transaction
 * @param currency - Devise (par défaut MAD)
 * @param transactionId - ID de la transaction (optionnel)
 * @param items - Liste des items (optionnel)
 */
export const trackPurchase = (
  value: number,
  currency: string = "MAD",
  transactionId?: string,
  items?: Array<{
    item_id?: string;
    item_name?: string;
    price?: number;
    quantity?: number;
  }>
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const eventProperties: Record<string, unknown> = {
      value,
      currency,
    };

    if (transactionId) {
      eventProperties.transaction_id = transactionId;
    }

    if (items && items.length > 0) {
      eventProperties.items = items;
    }

    trackEvent("purchase", eventProperties);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur Purchase:", error);
    }
  }
};

/**
 * Track un événement de début de checkout
 * @param value - Montant estimé
 * @param currency - Devise (par défaut MAD)
 */
export const trackBeginCheckout = (
  value?: number,
  currency: string = "MAD"
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const eventProperties: Record<string, unknown> = {};

    if (value !== undefined) {
      eventProperties.value = value;
      eventProperties.currency = currency;
    }

    trackEvent("begin_checkout", eventProperties);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur BeginCheckout:", error);
    }
  }
};

/**
 * Track un événement de vue de contenu (vue d'un véhicule)
 * @param itemId - ID du véhicule
 * @param itemName - Nom du véhicule
 * @param value - Prix journalier (optionnel)
 * @param currency - Devise (par défaut MAD)
 * @param additionalProperties - Propriétés supplémentaires (vehicle_type, city, price_range, etc.)
 */
export const trackViewItem = (
  itemId: string,
  itemName: string,
  value?: number,
  currency: string = "MAD",
  additionalProperties?: Record<string, unknown>
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    const eventProperties: Record<string, unknown> = {
      item_id: itemId,
      item_name: itemName,
    };

    if (value !== undefined) {
      eventProperties.price = value;
      eventProperties.currency = currency;
    }

    // Ajouter les propriétés supplémentaires si fournies
    if (additionalProperties) {
      Object.assign(eventProperties, additionalProperties);
    }

    trackEvent("view_item", eventProperties);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur ViewItem:", error);
    }
  }
};

/**
 * Track un événement de génération de lead (inscription, création de compte)
 */
export const trackLead = (): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("generate_lead");
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur GenerateLead:", error);
    }
  }
};

/**
 * Track un clic sur un produit dans les résultats de recherche
 * @param vehicleId - ID du véhicule
 * @param vehicleName - Nom du véhicule
 * @param price - Prix journalier
 * @param currency - Devise (par défaut MAD)
 * @param category - Catégorie du véhicule (optionnel)
 * @param position - Position dans la liste (optionnel)
 * @param imageUrl - URL de l'image (optionnel)
 * @param city - Ville du véhicule (optionnel)
 */
export const trackProductClicked = (
  vehicleId: string,
  vehicleName: string,
  price: number,
  currency: string = "MAD",
  category?: string,
  position?: number,
  imageUrl?: string,
  city?: string
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("product_clicked", {
      product_id: vehicleId,
      name: vehicleName,
      price,
      currency,
      category,
      position,
      url: typeof window !== "undefined" ? window.location.href : "",
      image_url: imageUrl,
      city,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur ProductClicked:", error);
    }
  }
};

/**
 * Track une recherche de véhicules
 * @param searchQuery - Terme de recherche (optionnel)
 * @param searchLocation - Localisation recherchée (optionnel)
 * @param dateFrom - Date de début (optionnel)
 * @param dateTo - Date de fin (optionnel)
 * @param resultsCount - Nombre de résultats (optionnel)
 */
export const trackSearch = (
  searchQuery?: string,
  searchLocation?: string,
  dateFrom?: string,
  dateTo?: string,
  resultsCount?: number
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("search", {
      search_query: searchQuery,
      search_location: searchLocation,
      date_from: dateFrom,
      date_to: dateTo,
      results_count: resultsCount,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur Search:", error);
    }
  }
};

/**
 * Track l'ajout au panier (options sélectionnées lors de la réservation)
 * @param vehicleId - ID du véhicule
 * @param vehicleName - Nom du véhicule
 * @param totalPrice - Prix total
 * @param currency - Devise (par défaut MAD)
 * @param durationDays - Durée en jours
 * @param extras - Options supplémentaires (optionnel)
 */
export const trackAddToCart = (
  vehicleId: string,
  vehicleName: string,
  totalPrice: number,
  currency: string = "MAD",
  durationDays: number,
  extras?: string[]
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("add_to_cart", {
      product_id: vehicleId,
      name: vehicleName,
      price: totalPrice,
      currency,
      duration_days: durationDays,
      extras: extras || [],
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur AddToCart:", error);
    }
  }
};

/**
 * Track une erreur de paiement
 * @param transactionId - ID de la transaction
 * @param errorMessage - Message d'erreur
 * @param errorCode - Code d'erreur (optionnel)
 * @param paymentMethod - Méthode de paiement (optionnel)
 * @param amount - Montant (optionnel)
 * @param currency - Devise (par défaut MAD)
 */
export const trackPaymentFailed = (
  transactionId: string,
  errorMessage: string,
  errorCode?: string,
  paymentMethod?: string,
  amount?: number,
  currency: string = "MAD"
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("payment_failed", {
      transaction_id: transactionId,
      error_message: errorMessage,
      error_code: errorCode,
      payment_method: paymentMethod,
      value: amount,
      currency,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur PaymentFailed:", error);
    }
  }
};

/**
 * Track l'abandon de checkout
 * @param vehicleId - ID du véhicule
 * @param totalPrice - Prix total
 * @param currency - Devise (par défaut MAD)
 * @param step - Étape où l'abandon a eu lieu (optionnel)
 * @param timeSpent - Temps passé en secondes (optionnel)
 */
export const trackCheckoutAbandoned = (
  vehicleId: string,
  totalPrice: number,
  currency: string = "MAD",
  step?: string,
  timeSpent?: number
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("checkout_abandoned", {
      step,
      vehicle_id: vehicleId,
      value: totalPrice,
      currency,
      time_spent: timeSpent,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur CheckoutAbandoned:", error);
    }
  }
};

/**
 * Track une étape de checkout complétée
 * @param vehicleId - ID du véhicule
 * @param step - Numéro de l'étape
 * @param stepName - Nom de l'étape
 */
export const trackCheckoutStepCompleted = (
  vehicleId: string,
  step: number,
  stepName: string
): void => {
  if (!isPostHogReady()) {
    return;
  }

  try {
    trackEvent("checkout_step_completed", {
      vehicle_id: vehicleId,
      step,
      step_name: stepName,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur CheckoutStepCompleted:", error);
    }
  }
};

export type SearchCardCtaType = "detail" | "quick_book" | "whatsapp";

export const trackSearchCardCtaClicked = (
  cta: SearchCardCtaType,
  vehicleId: string,
  hasDates: boolean
): void => {
  if (!isPostHogReady()) return;
  try {
    trackEvent("search_card_cta_clicked", {
      cta,
      vehicle_id: vehicleId,
      has_dates: hasDates,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur search_card_cta_clicked:", error);
    }
  }
};

export const trackCarDetailCalendarOpened = (
  auto: boolean,
  hasUrlDates: boolean
): void => {
  if (!isPostHogReady()) return;
  try {
    trackEvent("car_detail_calendar_opened", {
      auto,
      has_url_dates: hasUrlDates,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur car_detail_calendar_opened:", error);
    }
  }
};

export type QuickBookingOpenSource =
  | "search"
  | "car_detail_hero"
  | "car_detail_sticky"
  | "car_detail_sidebar";

export const trackQuickBookingOpened = (
  source: QuickBookingOpenSource,
  vehicleId: string
): void => {
  if (!isPostHogReady()) return;
  try {
    trackEvent("quick_booking_opened", {
      source,
      vehicle_id: vehicleId,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[PostHog] Erreur quick_booking_opened:", error);
    }
  }
};

/**
 * Fonction de diagnostic pour vérifier l'état de PostHog
 * À appeler depuis la console du navigateur pour debug
 */
export const diagnosePostHog = (): void => {
  const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';
  
  const diagnostics = {
    "PostHog Key": POSTHOG_KEY || "NON CONFIGURÉ",
    "PostHog Host": POSTHOG_HOST,
    "isPostHogReady()": isPostHogReady(),
    "window.posthog exists": typeof window !== "undefined" && typeof window.posthog !== "undefined",
    "posthog.capture exists": typeof window !== "undefined" && typeof window.posthog?.capture === "function",
    "Script in DOM": typeof document !== "undefined" && !!document.querySelector('script[src*="posthog.com/static/array.js"]'),
    "Distinct ID": typeof window !== "undefined" && window.posthog ? window.posthog.get_distinct_id() : "N/A",
  };

  console.log("=== DIAGNOSTIC POSTHOG ===");
  console.table(diagnostics);
  console.log("posthog object:", window.posthog);
  console.log("================================");

  return diagnostics;
};

// Exposer la fonction de diagnostic globalement pour faciliter le debug
if (typeof window !== "undefined") {
  (window as Window & { diagnosePostHog?: () => unknown }).diagnosePostHog = diagnosePostHog;
}

